<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { storeToRefs } from "pinia";
import { formatNumber } from "@/utils/helpers";
import { iniciarSesion, creditoUsuario } from "@/utils/usuarioRedirections";
import { useGarantiaStore } from "@/store/garantiaStore";
import { useUsuarioStore } from "@/store/usuarioStore";
import PreguntarModal from "./AlertasModal/PreguntarModal.vue";
import AceptarTerminosCondiciones from "./AlertasModal/AceptarTerminosCondiciones.vue";
import { Modal } from "bootstrap";
import ProrrogaModal from "./AlertasModal/ProrrogaModal.vue";
const props = defineProps({
  LoteStore: Object,
});

const garantiaStore = useGarantiaStore();
const usuarioStore = useUsuarioStore();
const { getCreditoUsuario, getUsuario } = storeToRefs(usuarioStore);

const loteStore = props.LoteStore;
const puja = computed(() => props.LoteStore.puja);
const panelOferta = computed(() => props.LoteStore.panelOferta);
const isOpenCargos = ref(false);
const isOpenOfertas = ref(false);
const preguntarAlert = ref(null);
const aceptarTerminosCondiciones = ref(null);
const prorrogaModal = ref(null);
const tiempoRestante = ref({
  dias: 0,
  horas: 0,
  minutos: 0,
  segundos: 0,
});

let interval = null;
let reloadInterval = null;
let ultimaProrrogaMostrada = null;

const pujaIniciada = ref(false);
const pujaFinalizada = ref(false);
const pujaEnCurso = ref(false);

const isCreditoSuficiente = computed(() => {
  const subasta = props.LoteStore.lote.subasta;

  if (subasta?.tipo_credito?.slug === "sin-garantia") return true;
  if (usuarioStore.user?.oferta_sin_garantia === true) return true;

  if (subasta?.privada && puedeVerSubasta.value) {
    return (
      getCreditoUsuario.value !== null &&
      getCreditoUsuario.value >= panelOferta?.value?.nueva_oferta
    );
  }
  return (
    getCreditoUsuario.value !== null &&
    getCreditoUsuario.value >= panelOferta?.value?.nueva_oferta
  );
});

const isSinTope = computed(() => {
  const subasta = props.LoteStore.lote.subasta;
  return (
    subasta?.tipo_credito?.slug === "sin-garantia" ||
    getUsuario.value?.oferta_sin_garantia === true
  );
});

const badgeClass = computed(() => {
  if (isSinTope.value) {
    return "badge bg-soft-green ms-3 pb-1 pt-1 fs-15 text-soft-green";
  }
  return isCreditoSuficiente.value
    ? "badge bg-soft-green ms-3 pb-1 pt-1 fs-15 text-soft-green"
    : "badge bg-danger text-white ms-3 pb-1 pt-1 fs-15";
});

const badgeCredito = computed(() => {
  const subasta = props.LoteStore.lote.subasta;

  if (subasta?.tipo_credito?.slug === "sin-garantia") return "Oferta sin garantía";
  if (getUsuario.value?.oferta_sin_garantia === true) return "Sin tope";

  return `$ ${formatNumber(getCreditoUsuario.value ?? 0)}`;
});

const puedeVerSubasta = computed(() => {
  const subasta = props.LoteStore.lote.subasta;

  if (!subasta?.privada) return true;

  const emailUsuario = usuarioStore.user?.email;
  if (!emailUsuario) return false;

  return subasta.usuarios_permitidos?.some(
    (usuario) => usuario.email === emailUsuario
  );
});

const calcularTiempoRestante = () => {
  if (!puja.value?.fecha_hora_cierre) return;

  const MS_DIA = 86400000,
    MS_HORA = 3600000,
    MS_MIN = 60000,
    MS_SEG = 1000;

  const cierre = new Date(puja.value.fecha_hora_cierre).getTime();
  const inicio = puja.value?.fecha_hora_inicio
    ? new Date(puja.value.fecha_hora_inicio).getTime()
    : null;
  const ahora = Date.now();

  pujaIniciada.value = typeof inicio === "number" && ahora >= inicio;

  const diferencia = cierre - ahora;

  if (diferencia <= 0) {
    pujaFinalizada.value = true;
    pujaEnCurso.value = false;
    tiempoRestante.value = { dias: 0, horas: 0, minutos: 0, segundos: 0 };
    clearInterval(interval);
    clearInterval(reloadInterval);
    return;
  }

  pujaEnCurso.value = pujaIniciada.value;

  if (!pujaEnCurso.value) {
    tiempoRestante.value = { dias: 0, horas: 0, minutos: 0, segundos: 0 };
    return;
  }

  tiempoRestante.value = {
    dias: Math.floor(diferencia / MS_DIA),
    horas: Math.floor((diferencia % MS_DIA) / MS_HORA),
    minutos: Math.floor((diferencia % MS_HORA) / MS_MIN),
    segundos: Math.floor((diferencia % MS_MIN) / MS_SEG),
  };
};

const toggleCollapseCargos = () => {
  isOpenCargos.value = !isOpenCargos.value;
};

const toggleCollapseOfertas = () => {
  isOpenOfertas.value = !isOpenOfertas.value;
};

onMounted(() => {
  calcularTiempoRestante();
  interval = setInterval(calcularTiempoRestante, 100);
  reloadInterval = setInterval(async () => {
    const claveProrroga = `prorroga_${props.LoteStore.lote.id}`;
    const fechaAnterior = new Date(props.LoteStore.puja.fecha_hora_cierre);

    if (props.LoteStore.puja) {
      await props.LoteStore.actualizacionOferta();
    }

    calcularTiempoRestante();

    const fechaNueva = new Date(props.LoteStore.puja.fecha_hora_cierre);
    const yaMostrada = localStorage.getItem(claveProrroga) === "true";

    const huboNuevaProrroga =
      fechaNueva > fechaAnterior &&
      (!ultimaProrrogaMostrada ||
        fechaNueva.getTime() !== ultimaProrrogaMostrada.getTime());

    const noSoyUltimaOferta =
      puja.value?.user_ultima_oferta_id !== getUsuario.value?.id;

    if (huboNuevaProrroga && yaMostrada && noSoyUltimaOferta) {
      abrirProrrogaModal();
      ultimaProrrogaMostrada = fechaNueva;
    }
  }, 5000);

  preguntarAlert.value = new Modal(document.getElementById("preguntarModal"), {
    backdrop: "static",
    keyboard: false,
  });

  aceptarTerminosCondiciones.value = new Modal(
    document.getElementById("aceptarTerminosCondiciones"),
    { backdrop: "static", keyboard: false }
  );

  prorrogaModal.value = new Modal(document.getElementById("prorrogaModal"), {
    backdrop: "static",
    keyboard: false,
  });
});

onUnmounted(() => {
  clearInterval(interval);
  clearInterval(reloadInterval);
});

function preguntar() {
  if (loteStore.aceptoTerminos) {
    preguntarAlert.value.show();
  } else {
    aceptarTerminosCondiciones.value.show();
  }
}

function abrirProrrogaModal() {
  prorrogaModal.value.show();
}
</script>
<template>
  <div
    v-if="LoteStore.lote.subasta?.privada && !puedeVerSubasta"
    class="card mb-3 tarjeta_ofertas"
  >
    <div class="card-body text-center me-3 ms-3">
      <h5 class="badge bg-soft-orange text-soft-organge fs-14 p-2">
        Subasta interna
      </h5>
      <div class="mt-2">
        <template v-if="!puja?.oferta_actual">
          <template v-if="!LoteStore.lote?.precio_inicial">
            <h6 class="bg-soft-grey p-2 text-center mb-2 rounded-2 text-subtitulos-secondary fw-semibold fs-6">
              Precio no establecido
            </h6>
          </template>
          <template v-else>
            <h2 class="text-soft-green fw-bold">
              <span class="me-1">$</span>{{ formatNumber(LoteStore.lote.precio_inicial) }}
            </h2>
          </template>
        </template>
        <template v-else>
          <h2 class="text-soft-green fw-bold">
            <span class="me-1">$</span>{{ formatNumber(puja.oferta_actual) }}
          </h2>
        </template>
      </div>
      <button
          class="btn btn-soft-green w-100 mb-4 mb-sm-2 fs-5 mt-2"
          v-if="!getUsuario"
          @click="iniciarSesion()"
        >
          Iniciar Sesión
      </button>
      <div class="d-flex mt-3 justify-content-center align-items-center">
        <i class="fa-brands fa-whatsapp text-success fs-5 me-2"></i>
        <a
          href="https://wa.me/5491140737966"
          target="_blank"
          rel="noopener"
          class="text-success text-decoration-underline fs-6 fw-bold"
        >
          ¿Necesitás ayuda?
        </a>
      </div>
    </div>
  </div>

  <!-- Información de la Subasta -->
  <div v-else class="card mb-3 tarjeta_ofertas">
    <div class="card-body text-center">
      <div class="d-inline-flex justify-content-center">
        <h5
          v-if="
            pujaFinalizada && puja?.user_ultima_oferta_id !== getUsuario?.id
          "
          class="p-2 m-1 pt-2 pb-2 mb-2 text-xs text-center badge bg-image-background text-black"
        >
          SUBASTA FINALIZADA
        </h5>
        <h5
          class="text-xs badge bg-soft-green text-soft-green p-2 m-1 pt-2 pb-2 mb-2"
          v-if="
            pujaFinalizada && puja?.user_ultima_oferta_id === getUsuario?.id
          "
        >
          ¡GANASTE!
        </h5>
        <h5
          class="text-xs text-soft-green p-2 m-1 pt-2 pb-2 mb-0 fs-14"
          v-else-if="
            !pujaFinalizada &&
            pujaIniciada &&
            (!getUsuario || puja?.user_ultima_oferta_id !== getUsuario?.id)
          "
        >
          OFERTA VIGENTE
        </h5>
        <h5
          v-else-if="
            pujaEnCurso && puja.user_ultima_oferta_id === getUsuario?.id
          "
          class="text-xs badge bg-soft-green text-soft-green p-2 m-1 pt-2 pb-2 mb-2 align-items-center"
        >
          <i class="fa-solid fa-circle-check text-soft-green me-1"></i>
          <span class="">¡VAS GANANDO!</span>
        </h5>
        <h5
          v-else-if="!pujaIniciada && loteStore.lote?.precio_inicial"
          class="text-xs badge bg-soft-green text-soft-green p-2 m-1 pt-2 pb-2 mb-2 align-items-center"
        >
          <i class="fa-solid fa-circle-check text-soft-green me-1"></i>
          <span class="">Precio inicial</span>
        </h5>
      </div>

      <h3
        class="fw-bolder fw-bold display-6 text-soft-green"
        v-if="pujaIniciada"
      >
        <span class="me-1">$</span>{{ formatNumber(puja.oferta_actual) }}
      </h3>
      <h3
        class="fw-bolder fw-bold display-6 text-soft-green"
        v-else-if="!pujaIniciada && loteStore.lote?.precio_inicial"
      >
        <span class="me-1">$</span
        >{{ formatNumber(loteStore.lote?.precio_inicial) }}
      </h3>
      <div>
        <h6 class="mb-0" v-if="pujaEnCurso && getUsuario">
          INCREMENTO + $ {{ formatNumber(puja.incremento) }}
        </h6>
        <h4
          class="fw-bolder display-6"
          :class="[{ 'text-danger': !isCreditoSuficiente }]"
          v-if="pujaEnCurso && getUsuario"
        >
          <span class="me-1">$</span
          >{{ formatNumber(panelOferta.nueva_oferta) }}
        </h4>
        <div
          class="d-flex flex-nowrap align-items-center justify-content-center gap-2 w-100 mt-3"
          v-if="getUsuario && pujaEnCurso"
        >
          <button
            class="decrementar btn px-4 shadow-none flex-fill"
            :class="[
              panelOferta.n > 1 ? 'btn-soft-green' : 'btn-light disabled',
            ]"
            @click="loteStore.decrementarOferta"
            :disabled="pujaFinalizada"
          >
            <i class="fas fa-minus text-white"></i>
          </button>

          <button
            class="btn text-white fw-bold shadow-none flex-fill text-center"
            style="width: 90%"
            :class="[
              panelOferta.n > 0 && isCreditoSuficiente
                ? 'btn-soft-green'
                : 'btn-light disabled',
            ]"
            @click="preguntar()"
            :disabled="!isCreditoSuficiente"
          >
            Ofertar
          </button>

          <button
            class="px-4 shadow-none flex-fill"
            :class="[
              'incrementar btn',
              isCreditoSuficiente &&
              puja.user_ultima_oferta_id !== getUsuario?.id
                ? 'btn-soft-green'
                : 'btn-light disabled',
            ]"
            :disabled="
              !isCreditoSuficiente ||
              puja.user_ultima_oferta_id === getUsuario?.id
            "
            @click="loteStore.incrementarOferta"
          >
            <i class="fas fa-plus text-white"></i>
          </button>
        </div>
        <p
          v-if="
            pujaEnCurso &&
            getUsuario &&
            puja?.user_ultima_oferta_id === getUsuario?.id
          "
          class="text-danger mt-2 fs-small fs-6"
        >
          Tu oferta es la más alta. Tenés que esperar nuevas ofertas para volver
          a pujar.
        </p>
        <p
          v-else-if="getUsuario && !isCreditoSuficiente"
          class="text-danger mt-2 fs-small fs-6"
        >
          Necesitas incrementar tu credito para seguir ofertando.
          <a
            href="javascript:;"
            class="text-danger text-decoration-underline"
            @click="creditoUsuario"
            >Solicitar crédito</a
          >
        </p>
        <div
          class="d-flex justify-content-between align-items-center w-100 mt-3"
          v-if="pujaEnCurso && getUsuario"
        >
          <h6 :class="['mb-0 fs-16', { 'text-danger': !isCreditoSuficiente }]">
            Crédito disponible
            <i
              class="fa-solid fa-circle-info fs-12"
              :class="{ 'text-danger': !isCreditoSuficiente }"
            ></i>
          </h6>
          <span :class="badgeClass">
            {{ badgeCredito }}
          </span>
        </div>

        <button
          class="btn btn-soft-green w-100 mb-4 mb-sm-2 fs-5 mt-2"
          v-if="!getUsuario"
          @click="iniciarSesion()"
        >
          Iniciar Sesión
        </button>
        <div class="text-center mt-3" v-if="pujaEnCurso">
          <p class="text-secondary mb-0">LA SUBASTA FINALIZA EN</p>
          <div
            class="d-flex flex-wrap justify-content-center bg-light p-2 gap-2 gap-md-4 fw-bold"
          >
            <div class="text-nowrap">{{ tiempoRestante.dias }} días</div>
            <div class="text-nowrap">{{ tiempoRestante.horas }} horas</div>
            <div class="text-nowrap">{{ tiempoRestante.minutos }} minutos</div>
            <div class="text-nowrap">
              {{ tiempoRestante.segundos }} segundos
            </div>
          </div>
        </div>
      </div>

      <div class="border border-1 rounded-2 mt-4 p-3 m-auto">
        <div class="d-flex justify-content-between aling-items-center">
          <p class="mb-0">Cargos y uso de plataforma</p>
          <i
            :class="isOpenCargos ? 'fa fa-angle-up' : 'fa fa-angle-down'"
            class="fs-5"
            style="cursor: pointer"
            @click="toggleCollapseCargos"
          ></i>
        </div>

        <div v-if="isOpenCargos" class="mt-3 d-flex flex-column gap-2">
          <div class="d-flex justify-content-between">
            <span class="text-start">Precio</span>
            <span class="text-end text-black"
              >$ {{ formatNumber(loteStore.cargos.precio) }}</span
            >
          </div>
          <div class="d-flex justify-content-between">
            <span class="text-start"
              >Uso de la plataforma ({{
                loteStore.cargos.porcentaje_uso_de_plataforma
              }}%)</span
            >
            <span class="text-end text-black"
              >$ {{ formatNumber(loteStore.cargos.uso_de_plataforma) }}</span
            >
          </div>
          <div class="d-flex justify-content-between">
            <span class="text-start"
              >Gastos administrativos ({{
                loteStore.cargos.porcentaje_gastos_administrativos
              }}%)</span
            >
            <span class="text-end text-black"
              >$
              {{ formatNumber(loteStore.cargos.gastos_administrativos) }}</span
            >
          </div>
          <div class="d-flex justify-content-between">
            <span class="text-start">Total (sin IVA) (*)</span>
            <span class="text-end text-black"
              >$ {{ formatNumber(loteStore.cargos.total_cargos) }}</span
            >
          </div>
          <p class="text-muted small mt-1 text-start mb-0">
            (*)Cálculo aproximado.
          </p>
        </div>
      </div>

      <div
        class="mt-3 border border-1 rounded-2 p-3 m-auto"
        v-if="pujaIniciada"
      >
        <div class="d-flex align-items-center gap-2">
          <p class="text-start mb-0">Historial</p>
          <div class="badge bg-soft-green text-soft-green">
            {{ puja.ofertas?.length }} ofertas
          </div>
          <i
            @click="toggleCollapseOfertas"
            :class="isOpenOfertas ? 'fa fa-angle-up' : 'fa fa-angle-down'"
            class="fa fa-angle-down fs-5 text-dark ms-auto"
          ></i>
        </div>
        <table class="table table-striped mt-3" v-if="isOpenOfertas">
          <tbody>
            <tr
              v-for="(oferta, index) in puja.ofertas
                ?.filter(
                  (o) =>
                    new Date(o.fecha_hora) >= new Date(puja.fecha_hora_inicio)
                )
                .slice(-5)
                .reverse()"
              :key="index"
            >
              <td>{{ oferta.nombre_usuario ?? "Anónimo" }}</td>
              <td class="small">
                <div>
                  {{
                    oferta.fecha_hora
                      .split(" ")[0]
                      .split("-")
                      .reverse()
                      .join("-")
                  }}
                </div>
                <div class="text-muted">
                  {{
                    oferta.fecha_hora
                      .split(" ")[1]
                      .replace(":", ".")
                      .replace(":", ".")
                  }}
                </div>
              </td>
              <td class="text-primary fw-bold">
                $ {{ formatNumber(oferta.oferta_actual) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="d-flex mt-4 justify-content-center align-items-center">
        <i class="fa-brands fa-whatsapp text-success fs-5 me-2"></i>
        <a
          href="https://wa.me/5491140737966"
          target="_blank"
          rel="noopener"
          class="text-success text-decoration-underline fs-6 fw-bold"
        >
          ¿Necesitás ayuda?
        </a>
      </div>
    </div>
  </div>
  <PreguntarModal
    :LoteStore="LoteStore"
    :garantiaStore="garantiaStore"
    :preguntarAlert="preguntarAlert"
    @abrirProrroOfertagaModal="abrirProrroOfertagaModal"
  />
  <AceptarTerminosCondiciones
    :LoteStore="LoteStore"
    :aceptarTerminosCondiciones="aceptarTerminosCondiciones"
    :preguntarAlert="preguntarAlert"
    @terminosEnviados="preguntar"
  />
  <ProrrogaModal
    :LoteStore="LoteStore"
    :prorrogaModal="prorrogaModal"
    :mostrarMensaje="mostrarMensaje"
  />
</template>
